import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { PremiumConfig, PremiumPrice } from 'src/api/internal/schema';
import { useUserContext } from 'src/context/user';
import { PaymentsApi } from 'src/api';
import { ROUTES } from 'src/constants/routes.const';
import useSafePush from 'src/hooks/useSafePush';
import { intlGetMessagesLocale } from 'src/utils/intl';
import { trackAndSaveCheckoutStarted } from 'src/analytics';
import { useBrazeContext } from 'src/context/braze';
import {
  getSessionStorageEntry,
  removeSessionStorageEntry,
  setSessionStorageEntry,
} from 'src/utils/webStorageUtils';
import { AUTO_SUBSCRIBE_AFTER_RELOAD_STORAGE_KEY } from 'src/constants/storageKeys.const';
import { getAnalyticsLanguageInfo } from 'src/utils/analytics/getAnalyticsLanguageInfo';
import { getAnalyticsUserInfo } from 'src/utils/analytics/getAnalyticsUserInfo';

const getStorageKeyForPrice = (price: PremiumPrice) =>
  `${price.key}:${price.discount?.key || 'no-discount'}`;

export type SubscribeHandlerConfig = {
  autoSubscribeToSinglePrice?: boolean;
};

export function useSubscribeHandler(
  premiumConfig: PremiumConfig | undefined,
  selectedPrice: PremiumPrice | null,
  { autoSubscribeToSinglePrice }: SubscribeHandlerConfig = {},
) {
  const intl = useIntl();
  const { safePush, safeReload } = useSafePush();
  const { loading: isLoadingUser, user } = useUserContext();
  const { appboy } = useBrazeContext();

  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [isWaitingForAuth, setIsWaitingForAuth] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const isLoading = isLoadingUser || isWaitingForAuth || isCreatingSession;

  const subscribeWithPrice = useCallback(
    async (price: PremiumPrice) => {
      if (!user || user.isPro) return;

      setIsCreatingSession(true);

      const urlParams = new URLSearchParams(window.location.search);
      const nextParam = urlParams.get('next');

      let successUrl = `${window.location.origin}${ROUTES.payment.confirmation}`;
      /* istanbul ignore next: please test in WEBSITE-7219 */
      if (nextParam) {
        successUrl += `?next=${encodeURIComponent(nextParam)}`;
      }

      const redirectUrl = await PaymentsApi.stripeCreateCheckoutSession({
        price,
        successUrl,
        locale: intlGetMessagesLocale(intl.locale),
      });

      trackAndSaveCheckoutStarted(appboy, user, intl, price);

      await safePush(redirectUrl);
    },
    [safePush, appboy, user, intl],
  );

  const subscribeHandler = useCallback(() => {
    if (!selectedPrice) {
      return;
    }

    // Track purchase click if user arrived from the upsell blocker with a tokenType
    /* istanbul ignore next: analytics */
    if (typeof window !== 'undefined' && window.location) {
      const urlParams = new URLSearchParams(window.location.search);
      const upsellTokenType = urlParams.get('upsellTokenType');
      if (upsellTokenType && window.dataLayer) {
        window.dataLayer.push({
          event: 'click_cta',
          action: `${upsellTokenType}_upsell_blocker_purchase`,
          click_area: 'Upsell blocker Purchase',
          page_screen_type: 'Upsell blocker',
          page_screen_name: upsellTokenType,
          ...getAnalyticsLanguageInfo(intl),
          ...getAnalyticsUserInfo(user),
        });
      }
    }

    if (!user && !isLoadingUser) {
      setIsWaitingForAuth(true);
      setSessionStorageEntry(
        AUTO_SUBSCRIBE_AFTER_RELOAD_STORAGE_KEY,
        getStorageKeyForPrice(selectedPrice),
      );
      return;
    }

    void (async () => {
      try {
        await subscribeWithPrice(selectedPrice);
      } catch (e) {
        console.error(e);
        setError(e);
      }
    })();
  }, [user, isLoadingUser, subscribeWithPrice, selectedPrice, intl]);

  const autoSubscribeToPrice = getSessionStorageEntry(AUTO_SUBSCRIBE_AFTER_RELOAD_STORAGE_KEY);

  useEffect(() => {
    if (!autoSubscribeToPrice || !premiumConfig || !user) return;

    removeSessionStorageEntry(AUTO_SUBSCRIBE_AFTER_RELOAD_STORAGE_KEY);

    const price = premiumConfig.prices.find(p => getStorageKeyForPrice(p) === autoSubscribeToPrice);

    if (price) {
      void (async () => {
        try {
          await subscribeWithPrice(price);
        } catch (e) {
          // if auto-subscribe failed just reload and allow the user to select the plan manually
          await safeReload();
        }
      })();
    }
  }, [autoSubscribeToPrice, premiumConfig, subscribeWithPrice, user, safeReload]);

  useEffect(() => {
    if (
      autoSubscribeToSinglePrice &&
      !isLoading &&
      !error &&
      premiumConfig &&
      premiumConfig.prices.length === 1 &&
      selectedPrice
    ) {
      subscribeHandler();
    }
  }, [
    autoSubscribeToSinglePrice,
    isLoading,
    error,
    premiumConfig,
    selectedPrice,
    subscribeHandler,
  ]);

  return {
    isLoading,
    isCreatingSession,
    isWaitingForAuth,
    error,
    subscribeHandler,
  };
}
